import { getUrlParamsDecoded } from "System/utilities/strings.js";
import langstore from 'System/i18n/translator.js';
import { runInAction } from "mobx";


const loadingKey = "ssoGemoActions";

/**
 * Request to gemo sso login
 * @param {string} path Path to the route to call, starting from ${this.config.paths.apiURL}profilo/ssologin/gemo/
 * @param {Object} params Params to be passed to the route 
 * @returns {Object} Response object
 */
let request = async function (path, params) {
  try {
    const response = await this.dataLayer({
      url: `${this.config.paths.apiURL}profilo/ssologin/gemo/${path}`,
      cacheAge: 0,
      params,
      rawResponse: true
    });

    return response;
  } catch (err) {
    throw err;
  }
}


/**
 * Reset Gemo sso page
 * @returns {void}
 */
let resetSsoPage = function () {
  runInAction(() => {
    this.gemoSsoStatus = {
      title: langstore.t("ssoGemoLogin_wait", "Attendere..."),
      message: "",
      showLoginLink: false
    };
  });

  return;
}

/**
 * Get valid users for the sso login
 * @param {String} id id string param
 * @param {String} userid userid string param
 * @param {Number} cliente cliente number
 * @returns {Array<Object>} Array of users with relative login token
 */
const getUsers = async function (id, userid, cliente) {
  try {
    const response = await request('users', { id, userid, cliente });

    const { status, statusCode, data } = response || {};
    const { users } = data || {};

    if (status == "ERROR" && statusCode == "CLIENT_NOT_ENABLED") {
      throw new Error("CLIENT_NOT_ENABLED");
    }
    if (!users || status !== "OK" || !data || !users || users.length == 0) {
      throw new Error();
    }

    return users;
  } catch (err) {
    throw err;
  }
}

/**
 * Get authentication
 * @param {Object} user User data object (as returned from getUsers)
 * @returns {Object} Response object with authentication data
 */
const getAuthResponse = async function (user) {
  try {
    const response = await request('login', user);
    const { status, data } = response || {};
    const { auth } = data || {};

    if (!response || status !== "OK" || !data || !auth) {
      throw new Error();
    }

    return response;
  } catch (err) {
    throw err;
  }
}

/**
 * Show generic error message
 * @param {String} notificationMessage Optional notification message (will be shown as snackbar)
 */
let failSso = function (notificationMessage) {
  runInAction(() => {
    if (notificationMessage) {
      this.notificationAdd(notificationMessage, loadingKey);
    }

    this.gemoSsoStatus = {
      title: langstore.t("gemoSsoLogin_invalidLogin_title", "Errore"),
      message: langstore.t("gemoSsoLogin_invalidLogin_message", "Si \xE8 verificato un errore. Impossibile effettuare il login."),
      showLoginLink: true
    };

    this.loadingRemove(loadingKey);
  });
}

/**
 * Perform Gemo SSO login action
 * @returns {void}
 */
const ssoGemoActions = async function () {
  try {
    // Bind functions
    resetSsoPage = resetSsoPage.bind(this);
    failSso = failSso.bind(this);
    request = request.bind(this);

    // Reset sso page
    resetSsoPage();

    let users = null;
    let authResponse = null;
    const {
      index: route_index,
      document: route_document = "/ordini/dettaglio"
    } = this.config.routes;

    console.log(route_document)

    let {
      op,
      id,
      userid,
      cliente,
      ordine
    } = getUrlParamsDecoded();

    const [
      n_anno,
      c_numeratore,
      n_ordine
    ] = (ordine || '').split('-');

    // Preliminary checks - check if all needed params are present
    switch (op) {
      case 'DOC':
        if (!(id && userid && cliente && n_anno && c_numeratore && n_ordine)) {
          return failSso(langstore.t("sso_gemo_error_params", "Formato URL errato"));
        }
        break;
      case 'LOGIN':
        if (!(id && userid && cliente)) {
          return failSso(langstore.t("sso_gemo_error_params", "Formato URL errato"));
        }
        break;
      default:
        return failSso(langstore.t("sso_gemo_error_params", "Formato URL errato"));
    }

    // Cliente must be a number
    if (isNaN(cliente)) {
      return failSso(langstore.t("sso_gemo_error_params", "Formato URL errato"));
    }

    // Convert cliente to int
    cliente = parseInt(cliente);

    // showLoader
    this.loadingAdd(loadingKey);

    // Get users
    try {
      users = await getUsers(id, userid, cliente);
    } catch (err) {
      // If client not enabled then show invalid user page
      if (err.message === 'CLIENT_NOT_ENABLED') {
        runInAction(() => {
          this.gemoSsoStatus = {
            title: langstore.t("gemoSsoLogin_invalidUser_title", "Utente non abilitato"),
            message: "",
            showLoginLink: true
          };
          this.loadingRemove(loadingKey);
        });
      }
      // Else show generic error
      else {
        failSso();
      }

      return;
    }


    // Get auth data
    try {
      /**
       * Try login with first user in returned list
       * TODO: Will become selectable
       */

      authResponse = await getAuthResponse(users[0]);
    } catch (err) {
      failSso();

      return;
    }

    /**
     * Moved authentication below. There is risky, can produce an infinite loop (if there is an error after auth inside the code)
     * TODO: Check if infinite loop can be avoided 
     */
    // this.userAuth(authResponse);

    switch (op) {
      case 'LOGIN':
        this.userAuth(authResponse);
        this.routeChange(route_index);
        break;
      case 'DOC':
        // Join path parts
        const documentRoute = [
          route_document,
          n_anno,
          c_numeratore,
          n_ordine
        ].reduce((acc, part) => {
          // replace backslashes with forwardslashes, then remove heading and trailing forwardslashes
          part = part.replace(/\\/g, '/').replace(/^[\/]|[\/]$/g, '');

          return [acc, part].join("/");
        }, '');

        this.userAuth(authResponse);
        this.routeChange(documentRoute);
        break;
    }

    this.loadingRemove(loadingKey);
  } catch (err) {
    console.error(err);
    failSso();
  }
}


export {
  ssoGemoActions
}